// Added by Sejune Oh on 2019-03-19
// Copyright (c) Coreline Soft, All rights are reserved.
//////////////////////////////////////////////////////////////////////////////////////////////////////////////////////
import { Component } from 'react'
import { inject, observer } from 'mobx-react'
import { observable, action, computed, makeObservable } from 'mobx'
import { RouterProps } from 'react-router'

//////////////////////////////////////////////////////////////////////////////////////////////////////////////////////
// Define
import {
  ReactClickEvent,
  SORT_OPTION,
  ReactTablePagination,
  ReactInputChangeEvent,
  ROWS_PER_PAGE,
} from '@/helper/define'

// Util
import { parseUrlQuery, getQueryPath } from '@/helper/util'

// Logger
import Logger from '@/helper/Logger'

//////////////////////////////////////////////////////////////////////////////////////////////////////////////////////
// Components
import { default as TableComponent } from '../../Table/Table'
import TableHeader from '../../Table/TableHeader'
import TableBody from '../../Table/TableBody'
import TableFooter from '../../Table/TableFooter'

// Style
import { TABLE_ROW_WIDTH, TABLE_ROW_HEIGHT } from '@/styles/Theme'

//////////////////////////////////////////////////////////////////////////////////////////////////////////////////////
// Interface
import {
  TableBaseCProps as TableComponentInitProps,
  TableRowItemProps,
  TableOptionProps,
  TableUrlQueryProps,
  CustomSetterProps,
} from '../../Table'
import { UserPrefStore } from '@/stores/user/UserPref'

export interface StoreLinkedTableBaseCProps extends RouterProps, TableComponentInitProps {
  userPref?: UserPrefStore
  updateSearchKeyword?: (key: string) => void
  selectHook?: (arg: Array<number>) => void
}

//////////////////////////////////////////////////////////////////////////////////////////////////////////////////////
/**
 * [Function] StoreLinkedTable
 *
 * Custom Table을 생성합니다.
 *
 * - Generic
 * @P : Props
 * @II : Item Interface
 *
 * - Parameters
 * @param storeName: string
 *      Store의 이름을 전달.
 *
 */
export default function StoreLinkedTable<P, II>({
  storeName,
  selectedArr = [],
  option,
}: {
  storeName: string
  selectedArr?: Array<number>
  option?: TableOptionProps
}) {
  // Type for props
  type Props = P & StoreLinkedTableBaseCProps

  type IItem = II & TableRowItemProps

  //////////////////////////////////////////////////////////////////////////////////////////////////////////////////////
  @inject(storeName, 'userPref')
  @observer
  class Table extends Component<Props> {
    selectedIndexArr: Set<number> = new Set(selectedArr)
    isAllSelected: boolean = false
    getQueryParams: TableUrlQueryProps = {
      pageIndex: 0,
      pageSize: ROWS_PER_PAGE[0],
      orderBy: undefined,
      order: 'desc',
      filter: undefined,
    }
    nonenumerableElemCount: number = 0
    selectIndexHook?: (arg: Array<number>) => void

    // @ts-ignore
    mainStore = this.props[storeName]

    // @ts-ignore
    prefStore: UserPrefStore = this.props['userPref']

    unlistenHistory: any
    historyCtrRequired = this.props.isPaginationable

    constructor(props: Props) {
      super(props)
      makeObservable(this, {
        selectedIndexArr: observable,
        isAllSelected: observable,
        getQueryParams: observable,
        nonenumerableElemCount: observable,
        selectIndexHook: observable,
        componentDidMount: action,
        _getData: action,
        handleSort: action,
        handleSelectAll: action,
        handleSelectAllToggle: action,
        _selectAllTask: action,
        clearAllSelected: action,
        handleSelect: action,
        handleChangePage: action,
        handleChangePageSize: action,
        handleSearch: action,
        unselectItemById: action,
        selectItemById: action,
        setGetQueryParam: action,
        setSelectHook: action,
        selectedNum: computed,
        selectedIndex: computed,
        selectedIndexAsArray: computed,
        rowsNum: computed,
        colSpanCount: computed,
        customColSpanCount: computed,
        pageIndex: computed,
      })

      // @ts-ignore
      const rowsPerPage = this.prefStore.rowsPerPage! || ROWS_PER_PAGE[0]
      const parsedUrlQuery = parseUrlQuery(this.props.history.location.search, rowsPerPage)

      if (parsedUrlQuery) this.setGetQueryParam(parsedUrlQuery as TableUrlQueryProps)
      else this._getData()

      this.unlistenHistory = this.props.history.listen(async (location, action) => {
        if (action === 'POP') {
          const parsedUrlQuery = parseUrlQuery(location.search)
          if (parsedUrlQuery) this.setGetQueryParam(parsedUrlQuery as TableUrlQueryProps)
          else
            this.setGetQueryParam({
              page_index: '1',
              page_size: rowsPerPage!.toString(),
            })
        }
      })
    }

    componentDidMount() {
      this.getQueryParams.pageSize = this.prefStore.rowsPerPage || ROWS_PER_PAGE[0]

      if (this.props.selectHook) {
        this.setSelectHook(this.props.selectHook!)
      }
    }

    componentWillUnmount() {
      if (typeof this.unlistenHistory === 'function') {
        this.unlistenHistory()
      }
    }

    async _getData() {
      this.nonenumerableElemCount = 0

      const path = getQueryPath({ ...this.getQueryParams })
      const elements = await this.mainStore.getElements(path)

      if (!elements) {
        Logger.errLog('[Table Component] Can not get data. Check access token')
        return
      }

      if (this.props.customSetter) {
        const { name, args } = this.props.customSetter as CustomSetterProps
        await this.mainStore[name](elements, ...args)
      } else {
        await this.mainStore.setElements(elements)
      }

      this.mainStore.elements.map(({ nonenumerable }: any) => {
        if (nonenumerable) this.nonenumerableElemCount++
        return nonenumerable
      })

      if (this.nonenumerableElemCount === this.mainStore.elements.length) {
        this.isAllSelected = false
      } else {
        this.isAllSelected =
          this.selectedIndexArr.size === this.mainStore.elements.length - this.nonenumerableElemCount
      }
    }

    _historyPush = (path: string, savePrevPath = false) => {
      if (this.historyCtrRequired) {
        if (!savePrevPath) {
          this.props.history.push(path)
          return
        }

        this.props.history.push({
          pathname: path,
          state: {
            from: this.props.history.location.pathname,
          },
        })
      }
      Logger.devLog(`[Table] History Push >> ${path}`)
    }

    //////////////////////////////////////////////////////////////////////////////////////////////////////////////////////
    render() {
      const customColSpan = option ? option.colSpan || undefined : undefined
      return (
        <TableComponent
          tableHeadComponent={
            <TableHeader
              onSort={this.handleSort}
              onSelectAll={this.handleSelectAll}
              onSelectAllToggle={this.handleSelectAllToggle}
              isSortable={this.props.isSortable}
              isSelectable={this.props.isSelectable}
              headerProps={this.props.headerProps}
              isAllSelected={this.isAllSelected}
              selectedNum={this.selectedNum}
              rowsNum={this.rowsNum}
              order={this.getQueryParams.order}
              orderBy={this.getQueryParams.orderBy}
              defaultRowWidth={TABLE_ROW_WIDTH}
              customColSpan={customColSpan}
            />
          }
          tableBodyComponent={
            <TableBody
              onSelect={this.handleSelect}
              isSelectable={this.props.isSelectable}
              isRowSelectable={this.props.isRowSelectable}
              rows={this.mainStore.elements}
              emptyRowsCount={this.emptyRowsCount}
              colSpan={this.colSpanCount}
              onTableRowClick={this.handleTableRowClick}
              headerProps={this.props.headerProps}
              selectedIndexArr={this.selectedIndexArr}
              defaultRowHeight={TABLE_ROW_HEIGHT}
              customColSpan={customColSpan}
            />
          }
          tableFooterComponent={
            <TableFooter
              isPaginatable={this.props.isPaginationable}
              count={this.mainStore.totalCount}
              page={this.getQueryParams.pageIndex as number}
              rowsPerPage={this.getQueryParams.pageSize as number}
              rowsPerPageOptions={ROWS_PER_PAGE}
              colSpan={this.customColSpanCount}
              onPageChange={this.handleChangePage}
              onRowsPerPageChange={this.handleChangePageSize}
              info={this.props.info}
            />
          }
        />
      )
    }

    //////////////////////////////////////////////////////////////////////////////////////////////////////////////////////
    get selectedNum() {
      return this.selectedIndexArr.size
    }

    get selectedIndex() {
      return this.selectedIndexArr
    }

    get selectedIndexAsArray() {
      return Array.from(this.selectedIndexArr)
    }

    get rowsNum() {
      return this.mainStore.elements.length
    }

    get colSpanCount() {
      return this.props.headerProps.length * 2 + 1
    }
    get emptyRowsCount() {
      if (this.props.isPaginationable) return (this.getQueryParams.pageSize as number) - this.mainStore.elements.length

      return 0
    }

    get selectedList() {
      if (!this.props.isSelectable) return []

      if (this.isAllSelected) {
        return this.mainStore.elements.filter((item: IItem) => !item.nonenumerable)
      }

      return this.mainStore.elements.filter((item: IItem, index: number) => this.selectedIndexArr.has(index))
    }

    get customColSpanCount() {
      if (option && option.colSpan) {
        let colSpanCount = 0
        option.colSpan.forEach((num) => {
          colSpanCount += num
        })
        return colSpanCount + 1
      }

      return this.colSpanCount
    }

    get pageIndex() {
      return this.getQueryParams.pageIndex
    }

    //////////////////////////////////////////////////////////////////////////////////////////////////////////////////////
    handleSort = (prop: string) => {
      this.getQueryParams.order =
        this.getQueryParams.orderBy === prop && this.getQueryParams.order === 'desc' ? 'asc' : 'desc'
      this.getQueryParams.orderBy = prop
      this._getData()
    }

    handleSelectAll = (active: boolean) => {
      this.isAllSelected = active
      this._selectAllTask(active)
    }

    handleSelectAllToggle = () => {
      this.isAllSelected = !this.isAllSelected
      this._selectAllTask(this.isAllSelected)
    }

    _selectAllTask = (active: boolean) => {
      if (active) {
        this.mainStore.elements.map(({ nonenumerable }: any, index: number) => {
          if (!nonenumerable) this.selectedIndexArr.add(index)
          return index
        })
      } else {
        this.selectedIndexArr.clear()
      }

      if (this.selectIndexHook) this.selectIndexHook(Array.from(this.selectedIndexArr))
    }

    clearAllSelected = () => {
      this.isAllSelected = false
      this.selectedIndex.clear()

      if (this.selectIndexHook) this.selectIndexHook(Array.from(this.selectedIndexArr))
    }

    handleSelect = (index: number) => {
      if (this.selectedIndexArr.has(index)) {
        this.selectedIndexArr.delete(index)
        this.isAllSelected = false

        if (this.selectIndexHook) this.selectIndexHook(Array.from(this.selectedIndexArr))
        return
      }

      this.selectedIndexArr.add(index)
      this.isAllSelected = this.selectedIndexArr.size === this.mainStore.elements.length - this.nonenumerableElemCount
      if (this.selectIndexHook) this.selectIndexHook(Array.from(this.selectedIndexArr))
    }

    handleChangePage: ReactTablePagination = (pageEvent, page) => {
      this.getQueryParams.pageIndex = page
      this._getData()
    }

    handleChangePageSize: ReactInputChangeEvent = ({ target: { value } }): void => {
      this.getQueryParams.pageIndex = 0
      this.getQueryParams.pageSize = parseInt(value)
      this.prefStore.setRowsPerPage(value)
      this._getData()
    }

    handleSearch = (key: string, pageIndex = 0) => {
      this.getQueryParams.filter = key
      this.getQueryParams.pageIndex = pageIndex
      this._getData()
    }

    unselectItemById = (id: string) => {
      const index = this.mainStore.elements.findIndex((element: any) => element.id === id)
      this.selectedIndexArr.delete(index)
    }

    selectItemById = (id: string) => {
      const index = this.mainStore.elements.findIndex((element: any) => element.id === id)
      this.selectedIndexArr.add(index)
    }

    setGetQueryParam = ({ page_index, page_size, filter, order_by }: TableUrlQueryProps) => {
      this.getQueryParams.pageIndex = page_index ? parseInt(page_index) - 1 : 0
      this.getQueryParams.pageSize = page_size ? parseInt(page_size) : ROWS_PER_PAGE[0]
      this.getQueryParams.filter = filter
      if (this.props.updateSearchKeyword) this.props.updateSearchKeyword(filter || '')

      if (order_by) {
        const [orderBy, order] = (order_by as string).split(':')
        this.getQueryParams.orderBy = orderBy
        this.getQueryParams.order = order as SORT_OPTION
      } else {
        this.getQueryParams.orderBy = undefined
        this.getQueryParams.order = 'desc'
      }

      this._getData()
    }

    setSelectHook = (hook: any) => {
      this.selectIndexHook = hook
    }

    //////////////////////////////////////////////////////////////////////////////////////////////////////////////////////
    handleTableRowClick: ReactClickEvent = (event) => {
      const id = event.currentTarget.dataset.id
      if (option && option!.onRowClick) option!.onRowClick(event)
      else this._historyPush(`${this.props.basePath}detail/${id}`, true)
    }
  }

  return Table
}
